/* eslint-disable react/jsx-indent-props */
import React, { useEffect, Suspense, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../use.store';
import _ from 'lodash';
import { config, mapOption } from '../utils/constants';
import './App.css';
import { AdsignMap, loadMap } from '@adactiveasia/adsign-map';
import Loading from '../components/Loading';
import FloorSelector from '../components/FloorSelector';
import SearchCard from '../components/SearchCard';
import Search from './Search';
import PoiDetail from './PoiDetail';
import Direction from '../components/Direction';
import { getPoiByShape } from '../utils/functions';
import AmenitiesSelector from './AmenitiesSelector';
import Tooltip from '../components/Tooltip';

const Home = observer(() => {
    const sceneRef = React.useRef();
    const cameraControlsRef = React.useRef();
    const interactiveRef = React.useRef();
    const { mainStore, mapStore } = useStores();

    const renderMap = () => {
        return (
            <Suspense fallback={loadingMapComponent()}>
                <AdsignMap
                    interactiveRef={interactiveRef}
                    cameraControlsRef={cameraControlsRef}
                    sceneRef={sceneRef}
                    wayfindingOption={{
                        wayFindingType: 'play-back',
                        wayfindingSpeed: 5,
                    }}
                    mapOption={mapOption}
                    store={mapStore}
                    onShapeClick={(e) => {
                        onPoiClick(e);
                    }}
                />
            </Suspense>
        );
    };

    const loadingMapComponent = () => {
        return <div></div>;
    };

    const onPoiClick = (shape) => {
        try {
            if (mapStore.directions.length > 0) {
                handleExitWayfinding();
            }

            const selectedPoi = getPoiByShape(shape, mainStore.pois);

            if (selectedPoi) {
                if (mainStore.destinationPoi && !mainStore.startPoi) {
                    mainStore.setStartPoi(selectedPoi);
                    mainStore.setWayfindingShapeFrom(shape);
                    mapStore.setStartShape(shape);
                } else {
                    if (
                        mainStore.destinationPoi === selectedPoi &&
                        mainStore.startPoi !== selectedPoi
                    ) {
                        mainStore.setIsPoiOverlay(true);
                        mainStore.setSelectedPoiDetail(
                            mainStore.destinationPoi
                        );
                    }
                    mainStore.setDestinationPoi(selectedPoi);
                    mainStore.setWayfindingShapeTo(shape);
                    mapStore.setDestinationShape(shape);
                }
            } else {
                // setPoi(null);
                mapStore.setCurrentFreeShape([]);
            }
        } catch (e) {
            // console.log(e, shape, 'selectedPoi');
        }
    };

    const loadData = async () => {
        try {
            await mainStore.loadApp();

            mapStore.setConfigManually(config);

            await loadMap(mapStore);
        } catch (err) {
            // console.error('Failed to load map and app data', err);
        }
    };

    useEffect(() => {
        if (!mainStore.isAppLoaded) {
            loadData();
        }
    }, []); // run only once on mount

    useEffect(() => {
        if (mapStore.isMapLoaded) {
            mainStore.setProgressLoadApp(100);
        }
    }, [mapStore.isMapLoaded]);

    useEffect(() => {
        if (
            mapStore.directions.length > 0 &&
            (mapStore.isWayfindingSkipped ||
                (mapStore.isWayfindingFinished &&
                    mapStore.directions.length > 0))
        ) {
            try {
                interactiveRef.current.cameraFocusOnDirection(
                    mapStore,
                    mapStore.directions[mapStore.directions.length - 1]
                );
                mapStore.setCurrentDirection(mapStore.directions.length - 1);
            } catch (e) {}
        }
    }, [
        mapStore.directions.length,
        mapStore.isWayfindingFinished,
        mapStore.isWayfindingSkipped,
    ]);

    // show tooltip or detail poi
    useEffect(() => {
        if (
            (!mainStore.startPoi && mainStore.destinationPoi) ||
            (mainStore.startPoi &&
                mainStore.destinationPoi &&
                mainStore.startPoi?._id === mainStore.destinationPoi?._id)
        ) {
            setTimeout(() => mainStore.setIsShowTooltip(true), 400);
        } else if (
            mainStore.startPoi &&
            mainStore.destinationPoi &&
            mainStore.startPoi?._id !== mainStore.destinationPoi?._id
        ) {
            setTimeout(() => {
                mainStore.setIsPoiOverlay(true);
                mainStore.setSelectedPoiDetail(mainStore.destinationPoi);
            }, 400);
        }
    }, [mainStore.startPoi, mainStore.destinationPoi]);

    const handleExitWayfinding = () => {
        const currentFloorId = mapStore.activeFloorId;
        interactiveRef.current.resetMap();
        interactiveRef.current.changeActiveFloor(currentFloorId);

        mainStore.resetApp();
        mainStore.resetSearchHistory();

        mainStore.setStartPoi(null);
        mainStore.setDestinationPoi(null);

        mainStore.setWayfindingShapeFrom(null);
        mainStore.setWayfindingShapeTo(null);

        [...mapStore.freeShapes, ...mapStore.model3ds].forEach((e) => {
            if (e.topImage) interactiveRef.current.changeTopImage(e._id, null);
        });
    };

    return (
        <div className="App border border-1 border-black">
            <Loading />
            <div className="absolute right-1/2 translate-x-1/2 z-[60] w-full sm:w-[600px] space-y-3 px-4 sm:px-8 mt-10">
                <SearchCard
                    interactiveRef={interactiveRef}
                    handleExitWayfinding={handleExitWayfinding}
                />
            </div>

            <Tooltip />

            <Search interactiveRef={interactiveRef} />
            <PoiDetail interactiveRef={interactiveRef} />

            {renderMap()}

            {mapStore.directions.length > 0 ? (
                <Direction
                    interactiveRef={interactiveRef}
                    handleExitWayfinding={handleExitWayfinding}
                />
            ) : (
                <Fragment>
                    <div className="absolute bottom-[94px] left-1/2 -translate-x-1/2 z-[60] w-fit h-fit">
                        <FloorSelector interactiveRef={interactiveRef} />
                    </div>
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[60] w-fit h-fit">
                        <AmenitiesSelector interactiveRef={interactiveRef} />
                    </div>
                </Fragment>
            )}
        </div>
    );
});

export default Home;
